import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  ApiError,
  call,
  clearClock,
  readClock,
  setClock,
  type ClockStateResponse,
} from '../../api';
import { invalidateEverything } from '../../queryClient';

/**
 * Reading and moving the deployment's simulated clock (#358), for the one
 * control that does it.
 *
 * These hooks live beside the header rather than in `src/areas/queries.ts`
 * because the clock is not an area's data: it belongs to the shell, both areas
 * render the same control, and `areas/` already imports `components/` -- putting
 * them there would point the dependency both ways for one query key that
 * nothing else touches.
 */
export const CLOCK_QUERY_KEY = ['clock'] as const;

/**
 * What time this deployment thinks it is, or `null` where the question cannot
 * be asked.
 *
 * `null` is the whole discovery mechanism, and it is why this cannot be a
 * build-time flag. The frontend ships as one image for every environment: the
 * same bundle runs on staging, where the clock can be moved, and in production,
 * where the resource is not registered and `/clock` is a plain `404`. So the
 * control asks, and reads the answer:
 *
 *   - **404** -- this deployment has no clock override at all. Production.
 *   - **403** -- this principal may not read it. A machine client; nobody who
 *     renders a navbar.
 *
 * Both mean "render nothing", so both come back as `null` rather than as an
 * error: a header is not a place to show somebody a failure they cannot act on,
 * and a query left in an error state would retry and log for the entire life of
 * every production session.
 *
 * Anything else -- a 500, a dropped connection -- is still thrown. Those are
 * failures of a deployment that *does* have a clock, and silently hiding the
 * indicator on one would be hiding exactly the warning this feature exists to
 * give.
 */
export function useClock() {
  return useQuery<ClockStateResponse | null>({
    queryKey: CLOCK_QUERY_KEY,
    queryFn: async () => {
      try {
        return await call(readClock());
      } catch (error) {
        if (error instanceof ApiError && (error.isNotFound || error.isForbidden)) {
          return null;
        }
        throw error;
      }
    },
    // The clock advances on its own, so a cached reading goes stale by simply
    // being old. Half a minute is short enough that a reader never believes a
    // minute that has passed, and long enough that moving between screens does
    // not re-ask on every navigation.
    staleTime: 30_000,
  });
}

/**
 * Move the clock, or put it back.
 *
 * Both write the answer straight into the cache and then invalidate everything
 * else, in that order. The order is the point: the indicator has to change in
 * the same frame the button was pressed -- the person who just moved the clock a
 * year is the one who most needs to see that it moved -- while the screens
 * underneath refetch and swap their numbers when the answers arrive.
 */
function useClockMutation<TVariables>(
  mutationFn: (variables: TVariables) => Promise<ClockStateResponse>,
) {
  const queryClient = useQueryClient();
  return useMutation<ClockStateResponse, unknown, TVariables>({
    mutationFn,
    onSuccess: async (state) => {
      queryClient.setQueryData(CLOCK_QUERY_KEY, state);
      await invalidateEverything(queryClient);
    },
  });
}

/**
 * Set the simulated instant, from what a `<input type="datetime-local">` holds.
 *
 * The value is naive -- `2026-08-20T09:00` -- and is sent exactly as typed. The
 * API reads a naive datetime as São Paulo wall-clock time, which is what the
 * person typing into a Brazilian product meant; converting it to UTC here
 * would land the clock three hours from where they put it.
 */
export function useSetClock() {
  return useClockMutation<string>((simulatedNow) =>
    call(setClock({ body: { simulated_now: simulatedNow } })),
  );
}

export function useClearClock() {
  return useClockMutation<void>(() => call(clearClock()));
}
